import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Form, Grid, Label } from 'semantic-ui-react';
import ReactQuill from 'react-quill';
import './styles.scss';
import { useTranslation } from 'react-i18next';
import TRANSLATIONS from '@translations/translationNamespaces';
import clsx from 'clsx';
import { VALID_DOCUMENT_MIME_TYPES } from '@screens/page/const';

const Quill = ReactQuill.Quill;
const Inline = Quill.import('blots/inline');

let attachmentBlotInnerHtml = null;

class AttachmentBlot extends Inline {
  static create(url) {
    const node = super.create();
    node.setAttribute('href', url);
    node.setAttribute('target', '_blank');

    if (attachmentBlotInnerHtml) {
      node.innerHTML = attachmentBlotInnerHtml;
    }

    if (url) {
      node.onclick = () => window.open(url, '_blank');
    }

    node.setAttribute('title', node.textContent);

    return node;
  }

  static formats(domNode) {
    return domNode.getAttribute('href') || true;
  }

  format(name, value) {
    if (name === 'attachment' && value) {
      this.domNode.setAttribute('href', value);
    } else {
      super.format(name, value);
    }
  }

  formats() {
    const formats = super.formats();
    formats.attachment = AttachmentBlot.formats(this.domNode);
    return formats;
  }
}

AttachmentBlot.blotName = 'attachment';
AttachmentBlot.tagName = 'a';
Quill.register(AttachmentBlot);

const languages = ['pl'];
const languageFlags = {
  en: <i className="us flag" />,
  pl: <i className="pl flag" />,
};

const imageClassOptionsConfig = [
  { value: '', translationKey: 'richTextField.imageStyles.default' },
  { value: 'rich-image--full-width', translationKey: 'richTextField.imageStyles.fullWidth' },
  { value: 'rich-image--left', translationKey: 'richTextField.imageStyles.left' },
  { value: 'rich-image--right', translationKey: 'richTextField.imageStyles.right' },
];

const icons = Quill.import('ui/icons');
icons.attachment = '<i aria-hidden="true" class="black file icon"></i>';

const imageClassValues = imageClassOptionsConfig.filter(option => option.value).map(option => option.value);

const ImageBlot = Quill.import('formats/image');
class ImageWithClassBlot extends ImageBlot {
  static formats(domNode) {
    const formats = super.formats(domNode) || {};
    const matchedClass = imageClassValues.find(className => domNode.classList.contains(className));

    if (matchedClass) {
      formats.imageClass = matchedClass;
    }

    return formats;
  }

  format(name, value) {
    if (name === 'imageClass') {
      imageClassValues.forEach(className => {
        this.domNode.classList.remove(className);
      });

      if (value) {
        this.domNode.classList.add(value);
        this.domNode.setAttribute('data-image-class', value);
      } else {
        this.domNode.removeAttribute('data-image-class');
      }
    } else {
      super.format(name, value);
    }
  }
}
Quill.register(ImageWithClassBlot, true);

const TranslatableRichTextField = ({
  actions,
  label,
  name,
  onChange,
  value,
  errorMessage,
  disabled,
  labelClassName,
}) => {
  const quillRefs = useRef([...languages].map(() => React.createRef()));
  const [imageContextMenu, setImageContextMenu] = useState(null);
  const [t] = useTranslation(TRANSLATIONS.OFFERS);
  const [error] = useTranslation(TRANSLATIONS.ERRORS);
  const imageClassOptions = useMemo(
    () =>
      imageClassOptionsConfig.map(option => ({
        ...option,
        label: t(option.translationKey),
      })),
    [t],
  );

  const selectLocalImage = () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      const reader = new FileReader();
      const languageFromActiveElement = document.activeElement.parentElement.parentElement.getAttribute('id');

      if (file && actions.uploadSinglePhotoAction) {
        reader.onloadend = async () => {
          const uploadedFile = await actions.uploadSinglePhotoAction({ base64: reader.result, fileName: file.name });

          let range = quillRefs?.current?.[languageFromActiveElement].current.getEditor().getSelection(true);
          quillRefs.current[languageFromActiveElement].current.getEditor().setSelection(range.index + 1);
          quillRefs.current[languageFromActiveElement].current
            .getEditor()
            .insertEmbed(range.index, 'image', uploadedFile?.payload?.photo?.url);

          range = quillRefs?.current?.[languageFromActiveElement].current.getEditor().getSelection(true);
          quillRefs.current[languageFromActiveElement].current.getEditor().setSelection(range.index + 2);
        };

        return reader.readAsDataURL(file);
      }
    };
  };

  const selectAttachment = async () => {
    const input = document.createElement('input');
    attachmentBlotInnerHtml = prompt(t('enterFileName'));

    if (attachmentBlotInnerHtml) {
      input.setAttribute('type', 'file');
      input.setAttribute('accept', VALID_DOCUMENT_MIME_TYPES.join(', '));
      input.click();

      input.onchange = async () => {
        const file = input.files[0];
        const reader = new FileReader();
        const languageFromActiveElement = document.activeElement.parentElement.parentElement.getAttribute('id');

        if (file && actions.uploadSingleFileAction) {
          reader.onloadend = async () => {
            const uploadedFile = await actions.uploadSingleFileAction({
              base64: reader.result,
              fileName: file.name,
            });

            let range = quillRefs?.current?.[languageFromActiveElement].current.getEditor().getSelection(true);
            quillRefs.current[languageFromActiveElement].current.getEditor().setSelection(range.index + 1);
            quillRefs.current[languageFromActiveElement].current
              .getEditor()
              .insertEmbed(range.index, 'attachment', uploadedFile?.payload?.file?.url);

            range = quillRefs?.current?.[languageFromActiveElement].current.getEditor().getSelection(true);
            quillRefs.current[languageFromActiveElement].current
              .getEditor()
              .setSelection(range.index + attachmentBlotInnerHtml?.length);
            attachmentBlotInnerHtml = null;
          };

          return reader.readAsDataURL(file);
        }
      };
    }
  };

  useEffect(() => {
    const handleContextMenu = event => {
      const target = event.target instanceof Element ? event.target : null;

      if (!target) {
        return;
      }

      if (disabled) {
        return;
      }

      if (target.closest('.image-class-context-menu')) {
        return;
      }

      if (target.tagName === 'IMG' && target.closest('.ql-editor')) {
        event.preventDefault();
        const container = target.closest('.ql-container');
        const containerId = container?.getAttribute('id');
        const languageIndex = Number(containerId);
        const quill = Number.isNaN(languageIndex) ? null : quillRefs?.current?.[languageIndex]?.current?.getEditor();

        if (!quill) {
          return;
        }

        const currentClass = imageClassOptionsConfig.find(option => option.value && target.classList.contains(option.value));
        const blot = Quill.find(target);
        const imageIndex = blot ? quill.getIndex(blot) : null;

        if (imageIndex === null || Number.isNaN(imageIndex)) {
          return;
        }

        setImageContextMenu({
          node: target,
          className: currentClass?.value || '',
          languageIndex,
          imageIndex,
          position: {
            x: event.clientX,
            y: event.clientY,
          },
        });
        return;
      }

      if (!target.closest('.image-class-context-menu')) {
        setImageContextMenu(null);
      }
    };

    const handleClick = event => {
      const target = event.target instanceof Element ? event.target : null;

      if (target?.closest('.image-class-context-menu')) {
        return;
      }

      setImageContextMenu(null);
    };

    const handleScroll = () => {
      setImageContextMenu(null);
    };

    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('click', handleClick);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('click', handleClick);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [disabled]);

  const handleImageClassSelect = value => {
    if (!imageContextMenu || imageContextMenu.languageIndex === null || imageContextMenu.imageIndex === null) {
      return;
    }

    const quill = quillRefs?.current?.[imageContextMenu.languageIndex]?.current?.getEditor();

    if (!quill) {
      setImageContextMenu(null);
      return;
    }

    quill.formatText(imageContextMenu.imageIndex, 1, 'imageClass', value || false, 'user');

    setImageContextMenu(null);
  };

  const handleRemoveImage = () => {
    if (!imageContextMenu || imageContextMenu.languageIndex === null || imageContextMenu.imageIndex === null) {
      return;
    }

    const quill = quillRefs?.current?.[imageContextMenu.languageIndex]?.current?.getEditor();

    if (!quill) {
      setImageContextMenu(null);
      return;
    }

    quill.deleteText(imageContextMenu.imageIndex, 1, 'user');
    setImageContextMenu(null);
  };

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: 1 }, { header: 2 }],
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ script: 'sub' }, { script: 'super' }],
          [{ indent: '-1' }, { indent: '+1' }],
          [{ size: ['small', false, 'large', 'huge'] }],
          [{ color: [] }, { background: [] }],
          [{ align: [] }],
          ['image', 'link', 'attachment', 'blockquote'],
          ['clean'],
        ],
        handlers: {
          image: selectLocalImage,
          attachment: selectAttachment,
        },
      },
    }),
    [],
  );

  return (
    <>
      {languages.map((language, index) => {
        return (
          <Grid.Column key={`${name}-${language}`}>
            <Form.Field>
              {errorMessage && (
                <Label color="red" className={clsx('mb-5', labelClassName)}>
                  {error(errorMessage)}
                </Label>
              )}
              <label>
                {languageFlags[language]}
                {label}
              </label>
              <ReactQuill
                id={index}
                ref={quillRefs?.current?.[index]}
                modules={modules}
                formats={[
                  'header',
                  'font',
                  'background',
                  'color',
                  'code',
                  'size',
                  'bold',
                  'italic',
                  'underline',
                  'strike',
                  'blockquote',
                  'list',
                  'bullet',
                  'indent',
                  'script',
                  'align',
                  'direction',
                  'link',
                  'image',
                  'code-block',
                  'formula',
                  'audio',
                  'attachment',
                  'imageClass',
                ]}
                readOnly={disabled}
                theme="snow"
                index={index}
                name={name}
                value={value?.[language] ?? ' '}
                onChange={value => onChange(0, { name, value, language })}
              />
            </Form.Field>
          </Grid.Column>
        );
      })}
      {imageContextMenu?.node && (
        <div
          className="image-class-context-menu"
          style={{ top: `${imageContextMenu.position.y}px`, left: `${imageContextMenu.position.x}px` }}
        >
          <p>{t('richTextField.imageStyles.title')}</p>
          {imageClassOptions.map(option => (
            <button
              key={option.value || 'default'}
              type="button"
              className={clsx('image-class-context-menu__option', {
                'image-class-context-menu__option--active': imageContextMenu?.className === option.value,
              })}
              onClick={() => handleImageClassSelect(option.value)}
            >
              {option.label}
            </button>
          ))}
          <button
            type="button"
            className="image-class-context-menu__option image-class-context-menu__option--danger"
            onClick={handleRemoveImage}
          >
            {t('richTextField.imageStyles.remove')}
          </button>
        </div>
      )}
    </>
  );
};

TranslatableRichTextField.defaultProps = {
  label: '',
  name: '',
  onChange: () => {},
  value: '',
};

export default TranslatableRichTextField;
